import type { ReactNode } from "react";

import type { Options, ChartOptions, SeriesOptionsType, Point, PlotOptions, XAxisOptions } from "highcharts";
import { produce } from "immer";
import { renderToStaticMarkup } from "react-dom/server";

type SeriesType = SeriesOptionsType extends { type: infer T } ? T : never;

type FilterSeriesByType<T extends SeriesType> = Extract<SeriesOptionsType, { type: T }>;

type TooltipPoint = Point;

type TooltipPointData<Type extends SeriesType, TSeriesOptions extends object, TPointOptions extends object = object> = {
    point: TooltipPoint & { options: TPointOptions };
    options: FilterSeriesByType<Type> & TSeriesOptions;
};

type TooltipRenderFunction<
    Type extends SeriesType,
    TSeriesOptions extends object,
    TPointOptions extends object = object,
> = (points: Array<TooltipPointData<Type, TSeriesOptions, TPointOptions>>) => ReactNode;

// Methods are used via the builder pattern (new ChartConfigBuilder().withX().build()); fallow does not
// trace chained calls back to class members.
export class ChartConfigBuilder<
    Type extends SeriesType = SeriesType,
    TSeriesOptions extends object = object,
    TPointOptions extends object = object,
> {
    private config: Options = {
        xAxis: {
            crosshair: {
                width: 1,
                dashStyle: "ShortDash",
            },
        },
    };

    withStyle(style: ChartOptions): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            Object.assign(draft.chart || {}, style);
        });
        return this;
    }

    withXAxisLabelFormatter(
        formatter: (value: string | number) => string,
    ): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            draft.xAxis = {
                labels: {
                    formatter: function () {
                        return formatter(this.value);
                    },
                },
            };
        });
        return this;
    }

    withYAxisLabelFormatter(
        formatter: (value: string | number) => string,
    ): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            draft.yAxis = {
                labels: {
                    formatter: function () {
                        return formatter(this.value);
                    },
                },
            };
        });
        return this;
    }

    withChartTooltip(
        render: TooltipRenderFunction<Type, TSeriesOptions, TPointOptions>,
    ): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            draft.tooltip = {
                formatter: function () {
                    const points: Array<TooltipPointData<Type, TSeriesOptions, TPointOptions>> = this.points.map(
                        (point) => ({
                            point: point as TooltipPoint & { options: TPointOptions },
                            options: point.series.userOptions as FilterSeriesByType<Type> & TSeriesOptions,
                        }),
                    );
                    return renderToStaticMarkup(render(points));
                },
                useHTML: true,
                borderRadius: 3,
                shadow: { offsetX: 0 },
            };
        });
        return this;
    }

    withYAxisConfig(yAxisConfig: Partial<Options["yAxis"]>): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            draft.yAxis = {
                ...draft.yAxis,
                ...yAxisConfig,
            };
        });
        return this;
    }

    withXAxisConfig(xAxisConfig: Partial<Options["xAxis"]>): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            draft.xAxis = {
                ...draft.xAxis,
                ...xAxisConfig,
            };
        });
        return this;
    }

    withPlotOptions(plotOptions: PlotOptions): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            draft.plotOptions = plotOptions;
        });

        return this;
    }

    withCrosshair(crosshairConfig: XAxisOptions["crosshair"]): ChartConfigBuilder<Type, TSeriesOptions, TPointOptions> {
        this.config = produce(this.config, (draft) => {
            if (!draft.xAxis) {
                draft.xAxis = {};
            }
            if (Array.isArray(draft.xAxis)) {
                if (draft.xAxis[0]) {
                    draft.xAxis[0] = { ...draft.xAxis[0], crosshair: crosshairConfig };
                } else {
                    draft.xAxis[0] = { crosshair: crosshairConfig };
                }
            } else {
                (draft.xAxis as XAxisOptions).crosshair = crosshairConfig;
            }
        });

        return this;
    }

    build() {
        return this.config;
    }
}
